import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box } from "@mui/material";
import ProgressStepper from "./ProgressStepper";
import { ItemProgressBulkActions, saveItemProgress } from "./ItemProgress";
import {
  displayActiveStepToStoredStep,
  getProgressStepsForDisplay,
  hasStoredProgressStep,
  normalizeProductionProgressSteps,
  storedStepToDisplayActiveStep,
} from "../../common/util";

const STEP_CONTENT_TEXT =
  "Marking a step complete automatically marks the current + all prior steps complete. Marking a step in-progress only marks all prior steps complete.";

export default function ItemProgressTracker(props) {
  const { orderId, item, itemIndex, progress, onChange } = props;

  const productionSteps = normalizeProductionProgressSteps(progress.steps);
  const displaySteps = getProgressStepsForDisplay(progress.steps);
  const savedDisplayActive = storedStepToDisplayActiveStep(progress.step, productionSteps.length);
  const progressStarted = hasStoredProgressStep(progress.step);
  const canMarkDone = productionSteps.length > 0 && savedDisplayActive < productionSteps.length;

  const [activeStep, setActiveStep] = useState(savedDisplayActive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setActiveStep(savedDisplayActive);
  }, [progress.step]);

  const persistStep = useCallback(
    (step, nextDisplayStep) => {
      setSaving(true);
      setError("");
      const payload = {
        step,
        steps: productionSteps,
      };
      return saveItemProgress(orderId, itemIndex, item, payload)
        .then((res) => {
          setActiveStep(nextDisplayStep);
          if (onChange) {
            onChange(res.data);
          }
        })
        .catch((err) => {
          setError(err.response?.data?.message || "An error occurred while saving progress");
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [item, itemIndex, onChange, orderId, productionSteps]
  );

  const persistDisplayStep = useCallback(
    (nextDisplayStep) =>
      persistStep(displayActiveStepToStoredStep(nextDisplayStep), nextDisplayStep),
    [persistStep]
  );

  const handleContinue = () => {
    const next = Math.min(activeStep + 1, displaySteps.length);
    setActiveStep(next);
    persistDisplayStep(next);
  };

  const handleMarkInProgress = () => {
    persistDisplayStep(activeStep);
  };

  const handleClearProgress = () => {
    persistStep(null, 0);
  };

  const handleMarkDone = () => {
    persistStep(productionSteps.length, productionSteps.length);
  };

  return (
    <Box>
      <Box my={2}>
        <ProgressStepper
          orientation="vertical"
          steps={displaySteps}
          activeStep={activeStep}
          onContinue={handleContinue}
          onBack={handleMarkInProgress}
          onStepSelect={setActiveStep}
          savedDisplayActive={savedDisplayActive}
          progressStarted={progressStarted}
          actionsDisabled={saving}
          stepContentText={STEP_CONTENT_TEXT}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          mt: 2,
          pt: 2,
          borderTop: "1px solid #eee",
          "& > :not(:first-of-type)": { ml: 1 },
        }}
      >
        <ItemProgressBulkActions
          saving={saving}
          canClear={progressStarted}
          canMarkDone={canMarkDone}
          onClear={handleClearProgress}
          onMarkDone={handleMarkDone}
        />
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
}
